import { useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Textarea } from "~/components/ui/textarea";
import {
  addAdmin,
  AdminApiError,
  fetchAdmins,
  revokeAdmin,
} from "~/lib/admin-api";
import { dateDisplayProps, formatDateTime } from "~/lib/format";
import type { AdminUser } from "~/types/admin-users";

function shouldRetry(failureCount: number, error: Error) {
  if (
    error instanceof AdminApiError &&
    error.status >= 400 &&
    error.status < 500
  ) {
    return false;
  }
  return failureCount < 2;
}

const permissionErrorMessage =
  "管理者権限がありません。権限が変更された可能性があります — 再ログインしてください。";

function isPermissionError(error: AdminApiError): boolean {
  return (
    error.status === 401 ||
    error.status === 403 ||
    error.code === "FORBIDDEN" ||
    error.code === "ACTOR_NOT_ADMIN" ||
    error.code === "UNAUTHORIZED"
  );
}

function addErrorMessage(error: Error | null): string | null {
  if (!error) return null;

  if (error instanceof AdminApiError) {
    if (isPermissionError(error)) return permissionErrorMessage;

    switch (error.code) {
      case "USER_NOT_FOUND":
        return "このメールアドレスのユーザーが見つかりません（先にサインアップが必要です）";
      case "USER_AMBIGUOUS":
        return "同一メールの複数アカウントがあるため画面からは追加できません（要調査）";
      case "USER_BANNED":
        return "停止中のユーザーには付与できません";
      case "EMAIL_REQUIRED":
        return "メールアドレスを入力してください";
      case "REASON_REQUIRED":
        return "理由を入力してください";
    }
  }

  return "管理者を追加できませんでした。しばらくしてからもう一度お試しください。";
}

function revokeErrorMessage(error: Error | null): string | null {
  if (!error) return null;

  if (error instanceof AdminApiError) {
    if (isPermissionError(error)) return permissionErrorMessage;

    switch (error.code) {
      case "LAST_ADMIN":
        return "最後の管理者は解除できません";
      case "SELF_REVOKE_FORBIDDEN":
        return "自分自身の権限は解除できません";
      case "REASON_REQUIRED":
        return "理由を入力してください";
    }
  }

  return "管理者権限を解除できませんでした。しばらくしてからもう一度お試しください。";
}

function shortUserId(userId: string): string {
  return `${userId.slice(0, 8)}…`;
}

function adminLabel(admin: AdminUser): string {
  return admin.email ?? shortUserId(admin.userId);
}

type AddStep = "email" | "confirm";

export function AdminUsers({
  accessToken,
  currentUserId,
}: {
  accessToken: string;
  currentUserId: string;
}) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addStep, setAddStep] = useState<AddStep>("email");
  const [email, setEmail] = useState("");
  const [addReason, setAddReason] = useState("");
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null);
  const [revokeReason, setRevokeReason] = useState("");

  const query = useQuery({
    queryKey: ["admins", currentUserId],
    queryFn: ({ signal }) => fetchAdmins(accessToken, signal),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: shouldRetry,
  });

  const addMutation = useMutation({
    mutationFn: ({ email, reason }: { email: string; reason: string }) =>
      addAdmin(accessToken, { email, reason }),
    onSuccess: async (result) => {
      await query.refetch();
      closeAddDialog();
      toast.success(
        result.changed
          ? `${result.email} を管理者に追加しました。`
          : `${result.email} はすでに管理者です。`,
      );
    },
  });

  const revokeMutation = useMutation({
    mutationFn: ({ admin, reason }: { admin: AdminUser; reason: string }) =>
      revokeAdmin(accessToken, { userId: admin.userId, reason }),
    onSuccess: async (result, { admin }) => {
      await query.refetch();
      setSelectedAdmin(null);
      setRevokeReason("");
      const label = adminLabel(admin);
      toast.success(
        result.changed
          ? `${label} の admin 権限を解除しました。`
          : `${label} の admin 権限はすでに解除されていました。`,
      );
    },
  });

  function openAddDialog() {
    setAddStep("email");
    setEmail("");
    setAddReason("");
    addMutation.reset();
    setAddDialogOpen(true);
  }

  function closeAddDialog() {
    setAddDialogOpen(false);
    setAddStep("email");
    setEmail("");
    setAddReason("");
    addMutation.reset();
  }

  function handleAddDialogOpenChange(open: boolean) {
    if (open || addMutation.isPending) return;
    closeAddDialog();
  }

  function handleEmailSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    setEmail(trimmedEmail);
    setAddReason("");
    addMutation.reset();
    setAddStep("confirm");
  }

  function handleAddSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (addMutation.isPending) return;

    const trimmedReason = addReason.trim();
    if (!email || !trimmedReason) return;

    addMutation.mutate({ email, reason: trimmedReason });
  }

  function returnToEmailStep() {
    if (addMutation.isPending) return;
    setAddReason("");
    addMutation.reset();
    setAddStep("email");
  }

  function selectAdmin(admin: AdminUser) {
    setSelectedAdmin(admin);
    setRevokeReason("");
    revokeMutation.reset();
  }

  function handleRevokeDialogOpenChange(open: boolean) {
    if (open || revokeMutation.isPending) return;
    setSelectedAdmin(null);
    setRevokeReason("");
    revokeMutation.reset();
  }

  function handleRevokeSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedAdmin || revokeMutation.isPending) return;

    const trimmedReason = revokeReason.trim();
    if (!trimmedReason) return;

    revokeMutation.mutate({ admin: selectedAdmin, reason: trimmedReason });
  }

  if (query.error instanceof AdminApiError && query.error.status === 403) {
    return (
      <Alert variant="destructive" className="mx-auto max-w-2xl">
        <AlertTitle>権限がありません</AlertTitle>
        <AlertDescription>
          管理者の一覧表示と権限変更には admin 権限が必要です。
        </AlertDescription>
      </Alert>
    );
  }

  const addMutationError = addErrorMessage(addMutation.error);
  const revokeMutationError = revokeErrorMessage(revokeMutation.error);

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">管理者</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            admin 権限を持つユーザーを管理します。
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={openAddDialog}>管理者を追加</Button>
          <Button
            variant="outline"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching ? "更新中…" : "一覧を更新"}
          </Button>
        </div>
      </div>

      <Alert>
        <AlertDescription>
          admin 権限の剥奪は、対象ユーザーの次のリクエストから即時反映されます（再ログイン不要）。
        </AlertDescription>
      </Alert>

      {query.error && !query.data ? (
        <Alert variant="destructive">
          <AlertTitle>管理者一覧を取得できませんでした</AlertTitle>
          <AlertDescription>{query.error.message}</AlertDescription>
        </Alert>
      ) : null}

      {query.error && query.data ? (
        <Alert variant="warning">
          <AlertTitle>一覧の更新に失敗しました</AlertTitle>
          <AlertDescription>
            前回取得した内容を表示しています。{query.error.message}
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="grid min-h-80 place-items-center rounded-xl border bg-card text-sm text-muted-foreground">
          管理者一覧を読み込んでいます…
        </div>
      ) : null}

      {query.data ? (
        <Card>
          <CardContent className="grid gap-4 px-0">
            <div className="px-6">
              <p className="text-sm text-muted-foreground">
                管理者 {query.data.admins.length} 名
              </p>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">email</TableHead>
                  <TableHead>付与日</TableHead>
                  <TableHead>付与者</TableHead>
                  <TableHead className="pr-6 text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.admins.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="h-24 text-center text-muted-foreground"
                    >
                      管理者はいません。
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.admins.map((admin) => {
                    const isCurrentUser = admin.userId === currentUserId;

                    return (
                      <TableRow key={admin.userId}>
                        <TableCell className="pl-6 font-medium">
                          <span>{adminLabel(admin)}</span>
                          {isCurrentUser ? (
                            <span className="ml-2 text-xs text-muted-foreground">
                              （自分）
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell
                          {...dateDisplayProps(admin.grantedAt, formatDateTime)}
                        />
                        <TableCell>
                          {admin.grantedByEmail ?? shortUserId(admin.grantedBy)}
                        </TableCell>
                        <TableCell className="pr-6 text-right">
                          <div className="inline-grid justify-items-end gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => selectAdmin(admin)}
                              disabled={isCurrentUser}
                              title={
                                isCurrentUser
                                  ? "自分自身の admin 権限は解除できません"
                                  : undefined
                              }
                            >
                              管理者から外す
                            </Button>
                            {isCurrentUser ? (
                              <span className="text-xs text-muted-foreground">
                                自分自身は解除できません
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={addDialogOpen} onOpenChange={handleAddDialogOpenChange}>
        <DialogContent
          aria-labelledby="admin-add-dialog-title"
          aria-describedby="admin-add-dialog-description"
        >
          {addStep === "email" ? (
            <form className="grid gap-6" onSubmit={handleEmailSubmit}>
              <DialogHeader>
                <DialogTitle id="admin-add-dialog-title">
                  管理者を追加
                </DialogTitle>
                <DialogDescription id="admin-add-dialog-description">
                  サインアップ済みユーザーのメールアドレスを入力してください。
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-2 px-6">
                <Label htmlFor="admin-add-email">メールアドレス</Label>
                <Input
                  id="admin-add-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  placeholder="user@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={closeAddDialog}>
                  キャンセル
                </Button>
                <Button type="submit" disabled={email.trim().length === 0}>
                  確認へ
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <form className="grid gap-6" onSubmit={handleAddSubmit}>
              <DialogHeader>
                <DialogTitle id="admin-add-dialog-title">
                  管理者に追加しますか？
                </DialogTitle>
                <DialogDescription id="admin-add-dialog-description">
                  対象: <strong>{email}</strong>
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 px-6">
                {addMutationError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{addMutationError}</AlertDescription>
                  </Alert>
                ) : null}
                <div className="grid gap-2">
                  <Label htmlFor="admin-add-reason">追加理由（必須）</Label>
                  <Textarea
                    id="admin-add-reason"
                    name="reason"
                    autoFocus
                    required
                    placeholder="管理者に追加する理由を入力してください"
                    value={addReason}
                    onChange={(event) => {
                      setAddReason(event.target.value);
                      if (addMutation.error) addMutation.reset();
                    }}
                    disabled={addMutation.isPending}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={returnToEmailStep}
                  disabled={addMutation.isPending}
                >
                  戻る
                </Button>
                <Button
                  type="submit"
                  disabled={
                    addMutation.isPending || addReason.trim().length === 0
                  }
                >
                  {addMutation.isPending ? "追加中…" : "管理者に追加"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={selectedAdmin !== null}
        onOpenChange={handleRevokeDialogOpenChange}
      >
        <DialogContent
          aria-labelledby="admin-revoke-dialog-title"
          aria-describedby="admin-revoke-dialog-description"
        >
          {selectedAdmin ? (
            <form className="grid gap-6" onSubmit={handleRevokeSubmit}>
              <DialogHeader>
                <DialogTitle id="admin-revoke-dialog-title">
                  admin 権限を解除しますか？
                </DialogTitle>
                <DialogDescription id="admin-revoke-dialog-description">
                  対象: <strong>{adminLabel(selectedAdmin)}</strong>
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 px-6">
                {revokeMutationError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{revokeMutationError}</AlertDescription>
                  </Alert>
                ) : null}
                <div className="grid gap-2">
                  <Label htmlFor="admin-revoke-reason">解除理由（必須）</Label>
                  <Textarea
                    id="admin-revoke-reason"
                    name="reason"
                    autoFocus
                    required
                    placeholder="権限を解除する理由を入力してください"
                    value={revokeReason}
                    onChange={(event) => {
                      setRevokeReason(event.target.value);
                      if (revokeMutation.error) revokeMutation.reset();
                    }}
                    disabled={revokeMutation.isPending}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => handleRevokeDialogOpenChange(false)}
                  disabled={revokeMutation.isPending}
                >
                  キャンセル
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={
                    revokeMutation.isPending ||
                    revokeReason.trim().length === 0
                  }
                >
                  {revokeMutation.isPending ? "解除中…" : "管理者から外す"}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
